// frontend/src/app/sell/page.tsx
'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';

export default function SellPage() {
  const t = useTranslations('sell');

  const steps = [
    { n: '1', titleKey: 'step1Title', bodyKey: 'step1Body' },
    { n: '2', titleKey: 'step2Title', bodyKey: 'step2Body' },
    { n: '3', titleKey: 'step3Title', bodyKey: 'step3Body' },
  ] as const;

  const tiers = [
    { nameKey: 'freeName', priceKey: 'freePrice', shopsKey: 'freeShops', listingsKey: 'freeListings', highlight: false },
    { nameKey: 'proName', priceKey: 'proPrice', shopsKey: 'proShops', listingsKey: 'proListings', highlight: true },
    { nameKey: 'businessName', priceKey: 'businessPrice', shopsKey: 'businessShops', listingsKey: 'businessListings', highlight: false },
  ] as const;

  return (
    <main className="max-w-2xl mx-auto px-4 py-12 flex flex-col gap-12">

      {/* Hero */}
      <section className="text-center flex flex-col items-center gap-4">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1a2e1f] leading-tight">
          {t('heroTitle')}
        </h1>
        <p className="text-[#6b8c7a] text-base max-w-md">
          {t('heroSubtitle')}
        </p>
        <Link
          href="/sell/start/"
          className="mt-2 bg-[#2d7a4f] text-white rounded-xl px-8 py-3 text-sm font-semibold hover:bg-[#235f3d] transition"
        >
          {t('openShopBtn')}
        </Link>
      </section>

      {/* How it works */}
      <section>
        <h2 className="text-lg font-semibold text-[#1a2e1f] mb-6 text-center">{t('howItWorksTitle')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {steps.map(step => (
            <div key={step.n} className="bg-white border border-[#e8f0e8] rounded-xl p-5 flex flex-col gap-2">
              <div className="w-8 h-8 rounded-full bg-[#e8f5e9] text-[#2d7a4f] text-sm font-bold flex items-center justify-center">{step.n}</div>
              <p className="font-semibold text-[#1a2e1f] text-sm">{t(step.titleKey)}</p>
              <p className="text-[#6b8c7a] text-xs leading-relaxed">{t(step.bodyKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section>
        <h2 className="text-lg font-semibold text-[#1a2e1f] mb-6 text-center">{t('pricingTitle')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {tiers.map(tier => (
            <div
              key={tier.nameKey}
              className={`rounded-xl p-5 border flex flex-col gap-2 ${
                tier.highlight
                  ? 'bg-[#2d7a4f] text-white border-[#2d7a4f]'
                  : 'bg-white text-[#1a2e1f] border-[#e8f0e8]'
              }`}
            >
              <p className="font-bold text-base">{t(tier.nameKey)}</p>
              <p className={`text-2xl font-bold ${tier.highlight ? 'text-white' : 'text-[#2d7a4f]'}`}>{t(tier.priceKey)}</p>
              <p className={`text-xs ${tier.highlight ? 'text-[#c8e6c9]' : 'text-[#6b8c7a]'}`}>{t(tier.shopsKey)}</p>
              <p className={`text-xs ${tier.highlight ? 'text-[#c8e6c9]' : 'text-[#6b8c7a]'}`}>{t(tier.listingsKey)}</p>
              <p className={`text-xs mt-1 ${tier.highlight ? 'text-[#a8d5b5]' : 'text-[#adc5b5]'}`}>{t('perSale')}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#f0faf4] border border-[#c8e6c9] rounded-2xl p-8 text-center flex flex-col items-center gap-4">
        <h2 className="text-xl font-bold text-[#1a2e1f]">{t('ctaTitle')}</h2>
        <p className="text-sm text-[#6b8c7a] max-w-sm">
          {t('ctaSubtitle')}
        </p>
        <Link
          href="/sell/start/"
          className="bg-[#2d7a4f] text-white rounded-xl px-8 py-3 text-sm font-semibold hover:bg-[#235f3d] transition"
        >
          {t('openFreeShop')}
        </Link>
      </section>

    </main>
  );
}
